import type { FC, ReactNode } from 'react'
import type { Service } from '@/types'

import Image from 'next/image'
import { ArrowUpRight, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TransitionLink } from '@/components/generals/transition-link'

interface ServiceCardProps {
    service: Service
    className?: string
    sizes?: string
    useHomeTitle?: boolean
    featured?: boolean
}

export const ServiceCard: FC<ServiceCardProps> = ({
    service,
    className,
    sizes = '(min-width: 1024px) 40vw, (min-width: 768px) 50vw, 100vw',
    useHomeTitle = false,
    featured = false
}): ReactNode => {
    const title = useHomeTitle && service.homeTitle ? service.homeTitle : service.title

    return (
        <TransitionLink
            href={service.href}
            className={cn(
                'group relative isolate flex min-h-[22rem] flex-col justify-end overflow-hidden rounded-[28px] bg-ink p-6 text-cream sm:p-8',
                className
            )}
        >
            <Image
                src={service.image}
                alt={service.imageAlt}
                fill
                sizes={sizes}
                className='-z-20 object-cover transition-transform duration-[1400ms] ease-expo group-hover:scale-[1.06]'
            />

            <div
                aria-hidden='true'
                className='absolute inset-0 -z-10 bg-gradient-to-t from-ink via-ink/55 to-ink/5 transition-opacity duration-700 group-hover:opacity-90'
            />

            <div className='absolute inset-x-6 top-6 flex items-start justify-between gap-3 sm:inset-x-8 sm:top-8'>
                <span
                    className={cn(
                        'inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-mono text-[10.5px] uppercase tracking-[0.14em] backdrop-blur-md',
                        featured ? 'bg-ember text-white' : 'bg-ink/45 text-cream ring-1 ring-cream/15'
                    )}
                >
                    {featured ? (
                        <span className='relative flex size-1.5' aria-hidden='true'>
                            <span className='absolute inset-0 rounded-full bg-white animate-live' />
                            <span className='relative size-1.5 rounded-full bg-white' />
                        </span>
                    ) : (
                        <Clock className='size-3' aria-hidden='true' />
                    )}
                    {service.meta}
                </span>

                <span className='flex size-11 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream ring-1 ring-cream/20 backdrop-blur-md transition-[background-color,color,transform,translate,scale,rotate] duration-500 ease-expo group-hover:rotate-45 group-hover:bg-ember group-hover:text-white group-hover:ring-ember'>
                    <ArrowUpRight className='size-4' aria-hidden='true' />
                </span>
            </div>

            <h3 className={cn('font-heading font-semibold tracking-[-0.035em]', featured ? 'text-[2rem] leading-[1.02] sm:text-[2.6rem]' : 'text-[1.65rem] leading-[1.05] sm:text-[1.9rem]')}>
                {title}
            </h3>

            <p className='mt-3 max-w-md text-[15px] leading-relaxed text-cream/70'>
                {service.summary}
            </p>

            <p className='mt-6 flex items-center justify-between border-t border-cream/15 pt-4 font-mono text-[12px] uppercase tracking-[0.14em] text-cream'>
                {service.priceLabel}
                <span className='text-cream/50 transition-colors group-hover:text-ember-soft'>See the details</span>
            </p>
        </TransitionLink>
    )
}
